import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { NOTIFICATION_CATEGORIES_DISABLED_KEY } from "@shared/const";
import { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useSearch } from "wouter";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Settings as SettingsIcon, Key, Bell, Info, Lock, RefreshCw, Loader2, CheckCircle2, XCircle, MessageSquare, Plus, Trash2, Pencil, Mail, Copy, Eye, EyeOff, Zap, Send, Clock, GitBranch, ArrowLeft, ChevronRight, Bot, Globe, Megaphone, Instagram, Palette } from "lucide-react";
import { useAuth } from "@/_core/hooks/useAuth";
import { useDesktopNotifications } from "@/hooks/useDesktopNotifications";
import { toUserFriendlyError } from "@/lib/error-messages";

// ─── Registros DNS unificados ───────────────────────────────────────────────
// CNAMEs vêm do SendGrid (dnsRecords da API) — DKIM e envelope sender.
// TXTs (SPF + DMARC) são gerados pelo domínio — não são obrigatórios para o
// envio funcionar via automatic_security, mas Gmail e Outlook usam esses
// registros como sinais primários anti-spam: sem eles, e-mails podem cair
// em spam, e DMARC virou requisito duro do Gmail para envios >5k/dia.
type DnsRecord = {
  type: "CNAME" | "TXT";
  host: string;
  value: string;
};

function buildAllDnsRecords(
  domain: string,
  sendgridDnsRecords: Record<string, { host: string; data: string }> | null | undefined,
): DnsRecord[] {
  const cnames: DnsRecord[] = Object.values(sendgridDnsRecords ?? {}).map((record) => ({
    type: "CNAME",
    host: record.host,
    value: record.data,
  }));

  const txts: DnsRecord[] = [
    {
      type: "TXT",
      host: domain,
      value: "v=spf1 include:sendgrid.net ~all",
    },
    {
      type: "TXT",
      host: `_dmarc.${domain}`,
      value: `v=DMARC1; p=none; rua=mailto:dmarc@${domain}`,
    },
  ];

  return [...cnames, ...txts];
}
import UserManagementContent from "@/features/users/UserManagementContent";
import CrmSettingsPanel from "@/components/Crm/CrmSettingsPanel";
import McpTokensPanel from "@/components/McpTokensPanel";
import { usePermissions } from "@/hooks/usePermissions";
// UX-fix — chaves BYOK e webhooks viram sub-integrações dentro de Settings.
import LLMCredentialsPanel from "@/components/integrations/LLMCredentialsPanel";
import WebhooksManagerPanel from "@/components/integrations/WebhooksManagerPanel";
import MetaAdsPanel from "@/components/integrations/MetaAdsPanel";
import InstagramPanel from "@/components/integrations/InstagramPanel";
import CanvaPanel from "@/components/integrations/CanvaPanel";
import WhatsAppConnectPanel from "@/components/integrations/WhatsAppConnectPanel";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

function TenantWhatsAppField({ label, field, description, placeholder, type = "text", currentValue, onSave, isPending }: {
  label: string; field: string; description?: string; placeholder?: string; type?: string;
  currentValue: string; onSave: (value: string) => Promise<any>; isPending: boolean;
}) {
  const [local, setLocal] = useState("");
  const [showFeedback, setShowFeedback] = useState<"none" | "saved" | "error">("none");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (type === "password" && currentValue.includes("salvo")) return;
    if (currentValue) setLocal(currentValue);
  }, [currentValue, type]);

  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  const handleSave = async () => {
    if (!local.trim()) return;
    setSaving(true);
    try {
      await onSave(local);
      setShowFeedback("saved");
      if (type === "password") setLocal("");
    } catch { setShowFeedback("error"); }
    setSaving(false);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setShowFeedback("none"), 2500);
  };

  const isSaved = showFeedback === "saved";
  const isError = showFeedback === "error";

  return (
    <div className="space-y-1.5">
      <Label className="font-medium">{label}</Label>
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
      <div className="flex gap-2">
        <Input
          type={type}
          placeholder={type === "password" && currentValue.includes("salvo") ? "••••••••(token salvo — cole novo para alterar)" : placeholder}
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          className="font-mono text-sm"
        />
        <Button
          size="sm"
          onClick={handleSave}
          disabled={saving || isPending || !local.trim()}
          className={`shrink-0 gap-1 ${
            isSaved ? "!bg-green-600 hover:!bg-green-700 !text-white" :
            isError ? "!bg-red-600 hover:!bg-red-700 !text-white" : ""
          }`}
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          {!saving && isSaved && <><CheckCircle2 className="h-4 w-4" /> Salvo!</>}
          {!saving && isError && <><XCircle className="h-4 w-4" /> Erro</>}
          {!saving && !isSaved && !isError && "Salvar"}
        </Button>
      </div>
    </div>
  );
}

function SettingField({ label, keyName, description, placeholder, type = "text" }: {
  label: string; keyName: string; description?: string; placeholder?: string; type?: string;
}) {
  const { data: value, isLoading } = trpc.settings.get.useQuery({ key: keyName });
  const [local, setLocal] = useState("");
  const [showFeedback, setShowFeedback] = useState<"none" | "saved" | "error">("none");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const utils = trpc.useUtils();

  useEffect(() => {
    if (value !== undefined && value !== null) setLocal(value);
  }, [value]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  const setMut = trpc.settings.set.useMutation({
    onSuccess: () => {
      utils.settings.get.invalidate({ key: keyName });
      setShowFeedback("saved");
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setShowFeedback("none"), 2500);
    },
    onError: () => {
      setShowFeedback("error");
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setShowFeedback("none"), 2500);
    },
  });

  const isSaved = showFeedback === "saved";
  const isError = showFeedback === "error";

  return (
    <div className="space-y-1.5">
      <Label className="font-medium">{label}</Label>
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
      <div className="flex gap-2">
        <Input
          type={type}
          placeholder={isLoading ? "Carregando..." : placeholder}
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          disabled={isLoading}
          className="font-mono text-sm"
        />
        <Button
          size="sm"
          onClick={() => setMut.mutate({ key: keyName, value: local })}
          disabled={setMut.isPending || isLoading}
          className={`shrink-0 gap-1 ${
            isSaved ? "!bg-green-600 hover:!bg-green-700 !text-white" :
            isError ? "!bg-red-600 hover:!bg-red-700 !text-white" : ""
          }`}
        >
          {setMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {!setMut.isPending && isSaved && <><CheckCircle2 className="h-4 w-4" /> Salvo!</>}
          {!setMut.isPending && isError && <><XCircle className="h-4 w-4" /> Erro</>}
          {!setMut.isPending && !isSaved && !isError && "Salvar"}
        </Button>
      </div>
    </div>
  );
}

// Card de configuração de comportamento do robô. Lê/grava o setting
// `max_bot_attempts` via endpoints tipados (settings.getBotConfig /
// settings.updateBotConfig) — assim o backend valida range (1..10) e
// invalida o cache em memória do bot-handoff.
function BotBehaviorCard() {
  const { data, isLoading } = trpc.settings.getBotConfig.useQuery();
  const utils = trpc.useUtils();
  const [local, setLocal] = useState<number>(3);
  const [feedback, setFeedback] = useState<"none" | "saved" | "error">("none");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (data?.maxAttempts) setLocal(data.maxAttempts);
  }, [data?.maxAttempts]);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const mut = trpc.settings.updateBotConfig.useMutation({
    onSuccess: () => {
      utils.settings.getBotConfig.invalidate();
      setFeedback("saved");
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setFeedback("none"), 2500);
    },
    onError: () => {
      setFeedback("error");
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setFeedback("none"), 2500);
    },
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Bot className="h-4 w-4 text-primary" />
          Comportamento do Robô
        </CardTitle>
        <CardDescription className="mt-1">
          Quando o cliente responde algo fora do fluxo de automação, o robô tenta encaixar a resposta. Depois de N tentativas, transfere para um atendente humano.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <Label className="font-medium">Tentativas antes do handoff</Label>
          <p className="text-xs text-muted-foreground">
            Pedido explícito do cliente por atendente sempre transfere imediatamente, independente desse valor.
          </p>
          <div className="flex gap-2 items-center">
            <Input
              type="number"
              min={1}
              max={10}
              value={local}
              onChange={(e) => setLocal(Math.max(1, Math.min(10, Number(e.target.value) || 1)))}
              disabled={isLoading}
              className="w-24"
            />
            <Button
              size="sm"
              onClick={() => mut.mutate({ maxAttempts: local })}
              disabled={mut.isPending || isLoading || local === data?.maxAttempts}
              className={`gap-1 ${
                feedback === "saved" ? "!bg-green-600 hover:!bg-green-700 !text-white" :
                feedback === "error" ? "!bg-red-600 hover:!bg-red-700 !text-white" : ""
              }`}
            >
              {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {!mut.isPending && feedback === "saved" && <><CheckCircle2 className="h-4 w-4" /> Salvo!</>}
              {!mut.isPending && feedback === "error" && <><XCircle className="h-4 w-4" /> Erro</>}
              {!mut.isPending && feedback === "none" && "Salvar"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Card de notificações desktop POR USUÁRIO. Cada usuário decide se quer
// receber alertas do navegador e quais categorias o interessam — preferência
// guardada em localStorage (sem schema novo). Permissão do SO é solicitada
// via Notification API.
function DesktopNotificationsCard() {
  const { user } = useAuth();
  const { permission, prefs, adminDisabled, requestPermission, updatePref, isSupported } =
    useDesktopNotifications(user?.id ?? null);

  const categories: Array<{ key: import("@/hooks/useDesktopNotifications").NotificationCategory; label: string; description: string }> = [
    { key: "new_message", label: "Nova mensagem", description: "Quando um contato envia uma mensagem que cai na sua caixa de entrada." },
    { key: "handoff_to_human", label: "Transferência do robô", description: "Quando o robô transfere uma conversa para atendimento humano." },
    { key: "assigned_to_me", label: "Atribuída a mim", description: "Quando uma conversa é atribuída diretamente para você." },
  ];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Bell className="h-4 w-4 text-primary" />
          Notificações no navegador
        </CardTitle>
        <CardDescription className="mt-1">
          Receba notificações desktop do sistema operacional quando estiver com a aba aberta.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!isSupported && (
          <div className="text-xs text-muted-foreground bg-muted/40 rounded p-2">
            Este navegador não suporta notificações desktop (Safari iOS ou contexto não-HTTPS).
          </div>
        )}

        {isSupported && permission !== "granted" && (
          <div className="flex items-center justify-between gap-3 p-3 rounded bg-amber-50 border border-amber-200">
            <div className="text-sm">
              <p className="font-medium">Permissão não concedida</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {permission === "denied"
                  ? "Você negou as notificações. Para reativar, ajuste nas configurações do site no navegador."
                  : "Conceda permissão para receber alertas quando chegar uma mensagem nova."}
              </p>
            </div>
            <Button
              size="sm"
              onClick={requestPermission}
              disabled={permission === "denied"}
            >
              Habilitar
            </Button>
          </div>
        )}

        {isSupported && permission === "granted" && (
          <div className="text-xs text-green-700 bg-green-50 border border-green-200 rounded p-2 flex items-center gap-2">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Notificações habilitadas neste dispositivo.
          </div>
        )}

        <div className="space-y-2 pt-2">
          <Label className="font-medium text-sm">Quais alertas você quer receber</Label>
          {categories.map((cat) => {
            const blockedByAdmin = adminDisabled.has(cat.key);
            return (
              <div key={cat.key} className="flex items-start justify-between gap-3 py-1.5 border-t first:border-t-0">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{cat.label}</p>
                  <p className="text-xs text-muted-foreground">{cat.description}</p>
                  {blockedByAdmin && (
                    <p className="text-[11px] text-amber-700 mt-0.5">
                      Categoria desabilitada pelo administrador da conta.
                    </p>
                  )}
                </div>
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={prefs[cat.key] && !blockedByAdmin}
                  disabled={blockedByAdmin || permission !== "granted"}
                  onChange={(e) => updatePref(cat.key, e.target.checked)}
                />
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// Card de notificações para ADMINISTRADOR: define quais categorias estão
// liberadas para os usuários da conta escolherem. Cada usuário ainda decide
// individualmente, mas pode apenas dentro do que o admin permitir.
function AdminNotificationCategoriesCard() {
  const SETTING_KEY = NOTIFICATION_CATEGORIES_DISABLED_KEY;
  const { data: csv } = trpc.settings.get.useQuery({ key: SETTING_KEY });
  const utils = trpc.useUtils();
  const setMut = trpc.settings.set.useMutation({
    onSuccess: () => {
      utils.settings.get.invalidate({ key: SETTING_KEY });
      // A mesma chave é lida por `getNotificationPolicy` (usada pelo hook de
      // notificação do próprio admin). Sem esta invalidação, ele salvava e
      // continuava notificando pela categoria recém-desligada até o staleTime.
      utils.settings.getNotificationPolicy.invalidate();
    },
  });

  const allCategories = [
    { key: "new_message", label: "Nova mensagem" },
    { key: "handoff_to_human", label: "Transferência do robô" },
    { key: "assigned_to_me", label: "Atribuída a mim" },
  ];

  // Estado local optimista: parseia o CSV do servidor mas permite cliques
  // consecutivos sem aguardar o round-trip. Antes, o handler `toggle`
  // capturava `disabledSet` na closure do render — cliques rápidos
  // sobrescreviam um ao outro com estado stale.
  const serverDisabled = useMemo(() => {
    const s = new Set<string>();
    if (typeof csv === "string" && csv) {
      for (const part of csv.split(",")) {
        const trimmed = part.trim();
        if (trimmed) s.add(trimmed);
      }
    }
    return s;
  }, [csv]);

  const [optimistic, setOptimistic] = useState<Set<string> | null>(null);
  // Quando o servidor confirma uma mudança, descarta o estado optimista
  // e volta a refletir o servidor (single source of truth).
  useEffect(() => { setOptimistic(null); }, [csv]);

  const disabledSet = optimistic ?? serverDisabled;

  const toggle = (key: string, enable: boolean) => {
    // Trabalha sempre sobre o snapshot mais recente local — robusto a
    // cliques em sequência.
    const next = new Set(disabledSet);
    if (enable) next.delete(key); else next.add(key);
    setOptimistic(next);
    setMut.mutate({
      key: SETTING_KEY,
      value: Array.from(next).join(","),
      description: "Categorias de notificação desabilitadas pelo administrador (CSV)",
    }, {
      onError: () => setOptimistic(null), // rollback: volta a confiar no servidor
    });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <SettingsIcon className="h-4 w-4 text-primary" />
          Categorias liberadas para os usuários (admin)
        </CardTitle>
        <CardDescription className="mt-1">
          Cada usuário escolhe individualmente quais notificações quer receber. Aqui você define o catálogo permitido.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {allCategories.map((cat) => {
          const enabled = !disabledSet.has(cat.key);
          return (
            <div key={cat.key} className="flex items-center justify-between gap-3 py-1.5 border-t first:border-t-0">
              <p className="text-sm">{cat.label}</p>
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => toggle(cat.key, e.target.checked)}
              />
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

export default function Settings() {
  const [, navigate] = useLocation();
  const { can } = usePermissions();
  const canMcpTokens = can("settings:mcp_tokens");
  const search = useSearch();
  // `?integration=...` (deep link e volta do OAuth do Facebook/Canva) implica
  // a aba Integrações — sem isso a página abria em "Geral" com o recado
  // certo mas a integração escondida.
  const searchParams = new URLSearchParams(search);
  const activeTab = searchParams.get("tab") ?? (searchParams.get("integration") ? "integrations" : "general");
  const handleTabChange = (value: string) => {
    const next = new URLSearchParams(search);
    next.set("tab", value);
    navigate(`/settings?${next.toString()}`, { replace: true });
  };
  const [connectionStatus, setConnectionStatus] = useState<"idle" | "testing" | "ok" | "error">("idle");
  const [connectionInfo, setConnectionInfo] = useState<{ phoneNumber?: string; displayName?: string; error?: string } | null>(null);
  const [fieldForm, setFieldForm] = useState({ name: "", key: "", fieldType: "text" as "text" | "number" | "date" | "boolean" | "select", description: "", defaultValue: "", scope: "contact" as "contact" | "system", options: "" });
  const [showFieldForm, setShowFieldForm] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState<number | null>(null);
  const [fieldScope, setFieldScope] = useState<"contact" | "system">("contact");

  // Quick Replies state
  const [qrForm, setQrForm] = useState({ shortcut: "", content: "" });
  const [showQrForm, setShowQrForm] = useState(false);
  const [editingQrId, setEditingQrId] = useState<number | null>(null);

  // Office Hours state
  const DAY_NAMES = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
  const defaultDays = DAY_NAMES.map((_, i) => ({ dayOfWeek: i, isOpen: i >= 1 && i <= 5, startTime: "08:00", endTime: "18:00" }));
  const [ohDays, setOhDays] = useState(defaultDays);
  const [ohMessage, setOhMessage] = useState("");
  const [ohFlowId, setOhFlowId] = useState<string>("");
  const [ohEnabled, setOhEnabled] = useState<boolean>(true);

  // Default Flows state
  type FlowTypeKey = "welcome" | "default_reply" | "media" | "post_service";
  type DefaultFlowConfig = Record<FlowTypeKey, { flowId: string; isActive: boolean }>;
  const [dfConfig, setDfConfig] = useState<DefaultFlowConfig>({
    welcome: { flowId: "", isActive: true },
    default_reply: { flowId: "", isActive: true },
    media: { flowId: "", isActive: true },
    post_service: { flowId: "", isActive: true },
  });
  const [dfSavedTypes, setDfSavedTypes] = useState<Record<string, boolean>>({});

  // Conversation Starters state
  const [csForm, setCsForm] = useState({ buttonText: "", flowId: "" });
  const [showCsForm, setShowCsForm] = useState(false);
  const [editingCsId, setEditingCsId] = useState<number | null>(null);

  // Email Domains state
  const { user } = useAuth();
  const isSuperAdmin = !!(user as any)?.isSuperAdmin;
  const [selectedIntegration, setSelectedIntegration] = useState<
    "whatsapp" | "email" | "webhooks" | "llm" | "meta_ads" | "instagram" | "canva" | null
  >(null);

  // Suporta deep-link `?integration=webhooks|llm` — usado pelo
  // WebhookTriggerBanner do Flow Builder e por links de outros lugares.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const i = params.get("integration");
    if (
      i === "webhooks" ||
      i === "llm" ||
      i === "whatsapp" ||
      i === "email" ||
      i === "meta_ads" ||
      i === "instagram" ||
      i === "canva"
    ) {
      setSelectedIntegration(i);
    }

    // Recado da volta do OAuth do Canva (mesmo desenho do `meta` abaixo).
    const canva = params.get("canva");
    if (canva) {
      const recados: Record<string, { tipo: "ok" | "erro"; texto: string }> = {
        autorizado: { tipo: "ok", texto: "Canva conectado." },
        cancelado: { tipo: "erro", texto: "Você não concluiu a autorização no Canva." },
        sem_permissao: { tipo: "erro", texto: "Seu perfil não pode conectar o Canva." },
        nao_configurado: { tipo: "erro", texto: "A integração com o Canva ainda não está configurada neste ambiente." },
        sessao_divergente: { tipo: "erro", texto: "A conexão foi iniciada em outra sessão. Comece de novo por aqui." },
        state_invalido: { tipo: "erro", texto: "A autorização demorou demais e expirou. Tente novamente." },
        sem_codigo: { tipo: "erro", texto: "O Canva não devolveu a autorização." },
        falha_troca: { tipo: "erro", texto: "Não consegui concluir a conexão com o Canva. Tente de novo." },
        falha_registro: { tipo: "erro", texto: "O Canva não aceitou registrar a conexão agora. Tente de novo em instantes." },
      };
      const recado = recados[canva];
      if (recado?.tipo === "ok") toast.success(recado.texto);
      else if (recado) toast.error(recado.texto);
      params.delete("canva");
      const resto = params.toString();
      window.history.replaceState({}, "", resto ? `?${resto}` : window.location.pathname);
    }

    // Recado da volta do Login do Facebook. O servidor manda um CÓDIGO, não uma
    // frase: texto vindo da URL seria injeção de conteúdo, e a tradução pertence
    // à tela de qualquer forma.
    const meta = params.get("meta");
    if (meta) {
      const recados: Record<string, { tipo: "ok" | "erro"; texto: string }> = {
        autorizado: {
          tipo: "ok",
          texto: "Facebook autorizado. Escolha a conta abaixo para concluir.",
        },
        cancelado: {
          tipo: "erro",
          texto: "Você não concluiu a autorização no Facebook.",
        },
        escopos_incompletos: {
          tipo: "erro",
          texto:
            "Faltaram permissões na tela do Facebook. Refaça mantendo todas marcadas — sem elas " +
            "as mensagens não chegam.",
        },
        sem_permissao: {
          tipo: "erro",
          texto: "Seu perfil não pode gerenciar esta conexão.",
        },
        nao_configurado: {
          tipo: "erro",
          texto: "O Login do Facebook ainda não está configurado neste ambiente.",
        },
        sessao_divergente: {
          tipo: "erro",
          texto: "A conexão foi iniciada em outra sessão. Comece de novo por aqui.",
        },
        state_invalido: {
          tipo: "erro",
          texto: "A autorização demorou demais e expirou. Tente novamente.",
        },
        sem_codigo: { tipo: "erro", texto: "O Facebook não devolveu a autorização." },
        falhou: {
          tipo: "erro",
          texto: "Não consegui concluir a conexão com o Facebook. Tente de novo.",
        },
      };
      const recado = recados[meta];
      if (recado?.tipo === "ok") toast.success(recado.texto);
      else if (recado) toast.error(recado.texto);

      // Limpa o parâmetro para o recado não reaparecer a cada recarga da página.
      params.delete("meta");
      const resto = params.toString();
      window.history.replaceState({}, "", resto ? `?${resto}` : window.location.pathname);
    }
  }, []);
  const [newDomain, setNewDomain] = useState("");
  const [addingDomain, setAddingDomain] = useState(false);
  const [expandedDomainId, setExpandedDomainId] = useState<number | null>(null);
  const sgWebhookUrl = `${window.location.origin}/api/webhooks/sendgrid`;

  const { data: emailDomains = [], refetch: refetchDomains } = trpc.emailDomains.list.useQuery();
  const createDomainMut = trpc.emailDomains.create.useMutation();
  const validateDomainMut = trpc.emailDomains.validate.useMutation();
  const deleteDomainMut = trpc.emailDomains.delete.useMutation();

  const handleAddDomain = async () => {
    if (!newDomain.trim()) return;
    setAddingDomain(true);
    try {
      const result = await createDomainMut.mutateAsync({ domain: newDomain.trim().toLowerCase() });
      toast.success(`Domínio "${newDomain}" cadastrado! Configure os registros DNS abaixo.`);
      setNewDomain("");
      setExpandedDomainId(result.id);
      refetchDomains();
    } catch (error: any) {
      toast.error(toUserFriendlyError(error, "Erro ao cadastrar domínio"));
    } finally {
      setAddingDomain(false);
    }
  };

  const handleValidateDomain = async (id: number) => {
    try {
      const result = await validateDomainMut.mutateAsync({ id });
      if (result.valid) {
        toast.success("Domínio verificado com sucesso!");
      } else {
        // Resposta do SendGrid traz quais registros falharam; transforma em
        // mensagem específica em vez de "registros não detectados" genérico.
        const labels: Record<string, string> = {
          mail_cname: "CNAME do link branding (em####)",
          dkim1: "CNAME DKIM s1._domainkey",
          dkim2: "CNAME DKIM s2._domainkey",
          spf: "TXT SPF",
        };
        const failed = Object.entries(result.validationResults ?? {})
          .filter(([, r]) => !r.valid)
          .map(([key, r]) => {
            const label = labels[key] ?? key;
            return r.reason ? `${label} — ${r.reason}` : label;
          });
        if (failed.length > 0) {
          toast.error("Registros que ainda não validaram:", {
            description: failed.join(" · "),
            duration: 12000,
          });
        } else {
          toast.error("Registros DNS ainda não foram detectados. Verifique a configuração e tente novamente.");
        }
      }
      refetchDomains();
    } catch (error: any) {
      toast.error(toUserFriendlyError(error, "Erro ao validar domínio"));
    }
  };

  const handleDeleteDomain = async (id: number) => {
    if (!confirm("Tem certeza que deseja remover este domínio?")) return;
    try {
      await deleteDomainMut.mutateAsync({ id });
      toast.success("Domínio removido");
      refetchDomains();
    } catch (error: any) {
      toast.error(toUserFriendlyError(error, "Erro ao remover domínio"));
    }
  };

  // Remetente padrão
  const { data: senderConfig, refetch: refetchSender } = trpc.sendgrid.sender.get.useQuery();
  const updateSenderMut = trpc.sendgrid.sender.update.useMutation();
  const [senderEmailLocal, setSenderEmailLocal] = useState("");
  const [senderEmailUser, setSenderEmailUser] = useState("");
  const [senderEmailDomain, setSenderEmailDomain] = useState("");
  const [senderNameLocal, setSenderNameLocal] = useState("");
  const [savingSender, setSavingSender] = useState(false);

  const verifiedDomains = emailDomains.filter((d: any) => d.status === "verified");

  useEffect(() => {
    if (senderConfig) {
      setSenderEmailLocal(senderConfig.senderEmail || "");
      setSenderNameLocal(senderConfig.senderName || "");
      const [user, domain] = (senderConfig.senderEmail || "").split("@");
      setSenderEmailUser(user || "");
      setSenderEmailDomain(domain || "");
    }
  }, [senderConfig]);

  const handleSaveSender = async () => {
    const email = `${senderEmailUser.trim()}@${senderEmailDomain}`;
    if (!senderEmailUser.trim() || !senderEmailDomain) {
      toast.error("Preencha o usuário e selecione um domínio verificado");
      return;
    }
    setSavingSender(true);
    try {
      await updateSenderMut.mutateAsync({ senderEmail: email, senderName: senderNameLocal });
      toast.success("Remetente padrão salvo");
      refetchSender();
    } catch (error: any) {
      toast.error(toUserFriendlyError(error, "Erro ao salvar remetente"));
    } finally {
      setSavingSender(false);
    }
  };

  const [modoAvancadoWhatsApp, setModoAvancadoWhatsApp] = useState(false);
  const { data: tenantData, refetch: refetchTenant } = trpc.tenant.current.useQuery();
  const phoneNumberIdSetting = tenantData?.whatsappPhoneNumberId || "";
  const accessTokenSetting = tenantData?.hasWhatsappToken ? "(salvo)" : "";
  const updateTenantMut = trpc.tenant.update.useMutation({ onSuccess: () => refetchTenant() });
  const { data: webhookTokenData, refetch: refetchToken } = trpc.settings.ensureWebhookToken.useQuery();
  const utils = trpc.useUtils();
  const { data: fieldDefs = [] } = trpc.customFields.listDefs.useQuery();
  const resetFieldForm = () => { setShowFieldForm(false); setEditingFieldId(null); setFieldForm({ name: "", key: "", fieldType: "text", description: "", defaultValue: "", scope: fieldScope, options: "" }); };
  const createFieldMut = trpc.customFields.createDef.useMutation({
    onSuccess: () => { utils.customFields.listDefs.invalidate(); resetFieldForm(); toast.success("Campo criado!"); },
    onError: (e) => toast.error(e.message?.includes("unique") ? "Essa chave já existe." : toUserFriendlyError(e, "Erro ao salvar campo")),
  });
  const updateFieldMut = trpc.customFields.updateDef.useMutation({
    onSuccess: () => { utils.customFields.listDefs.invalidate(); resetFieldForm(); toast.success("Campo atualizado!"); },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao atualizar campo")),
  });
  const deleteFieldMut = trpc.customFields.deleteDef.useMutation({
    onSuccess: () => { utils.customFields.listDefs.invalidate(); toast.success("Campo excluído!"); },
  });

  // Quick Replies queries
  const { data: quickReplies = [] } = trpc.quickReplies.list.useQuery();
  const createQrMut = trpc.quickReplies.create.useMutation({
    onSuccess: () => { utils.quickReplies.list.invalidate(); setShowQrForm(false); setQrForm({ shortcut: "", content: "" }); toast.success("Resposta rápida criada!"); },
    onError: (e) => toast.error(e.message?.includes("unique") ? "Esse atalho já existe. Escolha outro nome." : toUserFriendlyError(e, "Erro ao salvar")),
  });
  const updateQrMut = trpc.quickReplies.update.useMutation({
    onSuccess: () => { utils.quickReplies.list.invalidate(); setShowQrForm(false); setEditingQrId(null); setQrForm({ shortcut: "", content: "" }); toast.success("Resposta rápida atualizada!"); },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao atualizar")),
  });
  const deleteQrMut = trpc.quickReplies.delete.useMutation({
    onSuccess: () => { utils.quickReplies.list.invalidate(); toast.success("Resposta rápida removida!"); },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao remover")),
  });

  // Office Hours queries
  const { data: ohData } = trpc.officeHours.get.useQuery();
  const { data: ohMessageSetting } = trpc.settings.get.useQuery({ key: "office_hours_message" });
  const { data: ohFlowIdSetting } = trpc.settings.get.useQuery({ key: "office_hours_flow_id" });
  const { data: ohEnabledSetting } = trpc.settings.get.useQuery({ key: "office_hours_enabled" });
  const { data: flowsList } = trpc.flows.list.useQuery();
  const updateOhMut = trpc.officeHours.update.useMutation({
    onSuccess: () => { utils.officeHours.get.invalidate(); toast.success("Horários salvos!"); },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao salvar horários")),
  });
  const setSettingMut = trpc.settings.set.useMutation({
    onSuccess: () => { utils.settings.get.invalidate(); },
  });

  // Default Flows queries
  const { data: dfData } = trpc.defaultFlows.get.useQuery();
  const updateDfMut = trpc.defaultFlows.update.useMutation({
    onSuccess: (_data, variables) => {
      utils.defaultFlows.get.invalidate();
      toast.success("Fluxo padrão salvo!");
      setDfSavedTypes(prev => ({ ...prev, [variables.flowType]: true }));
      setTimeout(() => setDfSavedTypes(prev => ({ ...prev, [variables.flowType]: false })), 2500);
    },
    onError: (e: any) => toast.error(toUserFriendlyError(e, "Erro ao salvar")),
  });

  // Conversation Starters queries
  const { data: csData = [] } = trpc.conversationStarters.list.useQuery();
  const createCsMut = trpc.conversationStarters.create.useMutation({
    onSuccess: () => { utils.conversationStarters.list.invalidate(); setShowCsForm(false); setCsForm({ buttonText: "", flowId: "" }); toast.success("Iniciador criado!"); },
    onError: (e: any) => toast.error(toUserFriendlyError(e, "Erro ao criar")),
  });
  const updateCsMut = trpc.conversationStarters.update.useMutation({
    onSuccess: () => { utils.conversationStarters.list.invalidate(); setShowCsForm(false); setEditingCsId(null); setCsForm({ buttonText: "", flowId: "" }); toast.success("Iniciador atualizado!"); },
    onError: (e: any) => toast.error(toUserFriendlyError(e, "Erro ao atualizar")),
  });
  const deleteCsMut = trpc.conversationStarters.delete.useMutation({
    onSuccess: () => { utils.conversationStarters.list.invalidate(); toast.success("Iniciador removido!"); },
    onError: (e: any) => toast.error(toUserFriendlyError(e, "Erro ao remover")),
  });

  // Load default flows config
  useEffect(() => {
    if (dfData && (dfData as any[]).length > 0) {
      const config = { ...dfConfig };
      for (const df of dfData as any[]) {
        const key = df.flowType as keyof DefaultFlowConfig;
        if (config[key]) {
          config[key] = { flowId: df.flowId ? String(df.flowId) : "", isActive: df.isActive };
        }
      }
      setDfConfig(config);
    }
  }, [dfData]);

  useEffect(() => {
    if (ohData && (ohData as any[]).length > 0) {
      const mapped = DAY_NAMES.map((_, i) => {
        const found = (ohData as any[]).find((d: any) => d.dayOfWeek === i);
        return found ? { dayOfWeek: i, isOpen: found.isOpen, startTime: found.startTime, endTime: found.endTime } : defaultDays[i];
      });
      setOhDays(mapped);
    }
  }, [ohData]);

  useEffect(() => { if (ohMessageSetting) setOhMessage(ohMessageSetting); }, [ohMessageSetting]);
  useEffect(() => { if (ohFlowIdSetting) setOhFlowId(ohFlowIdSetting); }, [ohFlowIdSetting]);
  useEffect(() => {
    if (ohEnabledSetting !== undefined && ohEnabledSetting !== null) setOhEnabled(ohEnabledSetting !== "false");
  }, [ohEnabledSetting]);

  const saveOfficeHours = () => {
    updateOhMut.mutate({ days: ohDays });
    if (ohMessage !== (ohMessageSetting ?? "")) setSettingMut.mutate({ key: "office_hours_message", value: ohMessage });
    if (ohFlowId !== (ohFlowIdSetting ?? "")) setSettingMut.mutate({ key: "office_hours_flow_id", value: ohFlowId });
    const currentEnabled = (ohEnabledSetting ?? "true") !== "false";
    if (ohEnabled !== currentEnabled) setSettingMut.mutate({ key: "office_hours_enabled", value: ohEnabled ? "true" : "false" });
  };

  const generateTokenMut = trpc.settings.generateWebhookToken.useMutation({
    onSuccess: () => {
      utils.settings.ensureWebhookToken.invalidate();
    },
  });

  const testConnectionMut = trpc.settings.testMetaConnection.useMutation({
    onSuccess: (data) => {
      if (data.success) {
        setConnectionStatus("ok");
        setConnectionInfo({ phoneNumber: data.phoneNumber, displayName: data.displayName });
        toast.success(`Conexão bem-sucedida! Número: ${data.phoneNumber}`);
      } else {
        setConnectionStatus("error");
        setConnectionInfo({ error: data.error });
        toast.error(`Falha na conexão: ${data.error}`);
      }
    },
    onError: (e) => {
      setConnectionStatus("error");
      setConnectionInfo({ error: e.message });
      toast.error(toUserFriendlyError(e, "Erro ao testar conexão"));
    },
  });

  const testConnection = () => {
    if (!tenantData?.whatsappPhoneNumberId || !tenantData?.hasWhatsappToken) {
      toast.error("Salve o Phone Number ID e o Access Token antes de testar");
      return;
    }
    setConnectionStatus("testing");
    testConnectionMut.mutate({});
  };

  return (
    <PermissionGuard permission="settings:view">
    <DashboardLayout>
      <div className="space-y-5 max-w-6xl">
        <div>
          <h1 className="text-2xl font-bold">Configurações</h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Configure as integrações e preferências do sistema
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList
            className={`w-full h-auto flex flex-nowrap overflow-x-auto justify-start md:grid md:overflow-visible ${
              canMcpTokens ? "md:grid-cols-10" : "md:grid-cols-9"
            }`}
          >
            <TabsTrigger value="general" className="shrink-0 whitespace-nowrap">Geral</TabsTrigger>
            <TabsTrigger value="office-hours" className="shrink-0 whitespace-nowrap">Horários</TabsTrigger>
            <TabsTrigger value="quick-replies" className="shrink-0 whitespace-nowrap">Respostas Rápidas</TabsTrigger>
            <TabsTrigger value="default-flows" className="shrink-0 whitespace-nowrap">Fluxos Padrão</TabsTrigger>
            <TabsTrigger value="fields" className="shrink-0 whitespace-nowrap">Campos</TabsTrigger>
            <TabsTrigger value="notifications" className="shrink-0 whitespace-nowrap">Notificações</TabsTrigger>
            <TabsTrigger value="users" className="shrink-0 whitespace-nowrap">Usuários</TabsTrigger>
            <TabsTrigger value="crm" className="shrink-0 whitespace-nowrap">CRM</TabsTrigger>
            <TabsTrigger value="integrations" className="shrink-0 whitespace-nowrap">Integrações</TabsTrigger>
            {canMcpTokens && (
              <TabsTrigger value="mcp" className="shrink-0 whitespace-nowrap">MCP</TabsTrigger>
            )}
          </TabsList>

          {/* MCP Tab — tokens de acesso do servidor MCP */}
          {canMcpTokens && (
            <TabsContent value="mcp" className="mt-4 space-y-4">
              <McpTokensPanel />
            </TabsContent>
          )}

          {/* Integrações Tab — hub + detalhe por integração */}
          <TabsContent value="integrations" className="mt-4 space-y-4">

            {/* Hub: grid de integrações */}
            {selectedIntegration === null && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* WhatsApp Business */}
                <button
                  type="button"
                  onClick={() => setSelectedIntegration("whatsapp")}
                  className="text-left border rounded-lg p-4 hover:border-primary/50 hover:bg-muted/30 transition-colors group"
                >
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-lg bg-green-100 dark:bg-green-950/30 flex items-center justify-center shrink-0">
                      <MessageSquare className="h-5 w-5 text-green-700 dark:text-green-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-medium text-sm">WhatsApp Business</h3>
                        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground shrink-0" />
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Envie mensagens pela API oficial da Meta
                      </p>
                      <div className="mt-2">
                        {tenantData?.hasWhatsappToken && tenantData?.whatsappPhoneNumberId ? (
                          <Badge variant="default" className="text-[10px]">Conectado</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-[10px]">Não configurado</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </button>

                {/* E-mail (SendGrid) */}
                <button
                  type="button"
                  onClick={() => setSelectedIntegration("email")}
                  className="text-left border rounded-lg p-4 hover:border-primary/50 hover:bg-muted/30 transition-colors group"
                >
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-lg bg-blue-100 dark:bg-blue-950/30 flex items-center justify-center shrink-0">
                      <Mail className="h-5 w-5 text-blue-700 dark:text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-medium text-sm">E-mail (SendGrid)</h3>
                        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground shrink-0" />
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Envie e-mails pelo seu domínio autenticado
                      </p>
                      <div className="mt-2">
                        {verifiedDomains.length > 0 && senderConfig?.senderEmail ? (
                          <Badge variant="default" className="text-[10px]">Configurado</Badge>
                        ) : verifiedDomains.length > 0 ? (
                          <Badge variant="secondary" className="text-[10px]">Falta remetente</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-[10px]">Não configurado</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </button>

                {/* Webhooks Externos — sub-página INTERNA do Settings (não
                    mais link para Automations). Configuração vive aqui. */}
                <button
                  type="button"
                  onClick={() => setSelectedIntegration("webhooks")}
                  className="text-left border rounded-lg p-4 hover:border-primary/50 hover:bg-muted/30 transition-colors group"
                >
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-lg bg-purple-100 dark:bg-purple-950/30 flex items-center justify-center shrink-0">
                      <Globe className="h-5 w-5 text-purple-700 dark:text-purple-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-medium text-sm">Webhooks Externos</h3>
                        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground shrink-0" />
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        URLs públicas para sistemas externos dispararem fluxos
                        (Zapier, Make, formulários, API própria)
                      </p>
                    </div>
                  </div>
                </button>

                {/* Chaves de API (BYOK) — sub-página INTERNA. Saiu do menu
                    lateral "Inteligência" porque BYOK é integração, não
                    feature de IA propriamente. */}
                <button
                  type="button"
                  onClick={() => setSelectedIntegration("llm")}
                  className="text-left border rounded-lg p-4 hover:border-primary/50 hover:bg-muted/30 transition-colors group"
                >
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-lg bg-amber-100 dark:bg-amber-950/30 flex items-center justify-center shrink-0">
                      <Key className="h-5 w-5 text-amber-700 dark:text-amber-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-medium text-sm">Chaves de API (BYOK)</h3>
                        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground shrink-0" />
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        OpenAI, Anthropic, Google ou OpenRouter — usadas pelos
                        Agentes IA. Custo de IA é do tenant (BYOK).
                      </p>
                    </div>
                  </div>
                </button>

                {/* Meta Ads — tráfego pago. A credencial fica aqui, junto das
                    outras integrações; a operação de campanha acontece pelo
                    assistente conectado (MCP) e na tela de Anúncios. */}
                <button
                  type="button"
                  onClick={() => setSelectedIntegration("meta_ads")}
                  className="text-left border rounded-lg p-4 hover:border-primary/50 hover:bg-muted/30 transition-colors group"
                >
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-lg bg-blue-100 dark:bg-blue-950/30 flex items-center justify-center shrink-0">
                      <Megaphone className="h-5 w-5 text-blue-700 dark:text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-medium text-sm">Meta Ads</h3>
                        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground shrink-0" />
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Conta de anúncios do Facebook e Instagram — acompanhe
                        gasto e gerencie campanhas de tráfego.
                      </p>
                    </div>
                  </div>
                </button>

                {/* Instagram como CANAL — separado do card de Meta Ads de
                    propósito: o token é outro (mensagens e comentários, não
                    anúncios), com escopos e ciclo de vida próprios. */}
                <button
                  type="button"
                  onClick={() => setSelectedIntegration("instagram")}
                  className="text-left border rounded-lg p-4 hover:border-primary/50 hover:bg-muted/30 transition-colors group"
                >
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-lg bg-pink-100 dark:bg-pink-950/30 flex items-center justify-center shrink-0">
                      <Instagram className="h-5 w-5 text-pink-700 dark:text-pink-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-medium text-sm">Instagram</h3>
                        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground shrink-0" />
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Mensagens diretas no Atendimento e comentários dos
                        posts numa fila de trabalho.
                      </p>
                    </div>
                  </div>
                </button>

                {/* Canva — propostas do CRM: abrir/editar no Canva da empresa e
                    trazer o PDF pronto; brand templates com Autofill no Enterprise. */}
                <button
                  type="button"
                  onClick={() => setSelectedIntegration("canva")}
                  className="text-left border rounded-lg p-4 hover:border-primary/50 hover:bg-muted/30 transition-colors group"
                >
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-lg bg-violet-100 dark:bg-violet-950/30 flex items-center justify-center shrink-0">
                      <Palette className="h-5 w-5 text-violet-700 dark:text-violet-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-medium text-sm">Canva</h3>
                        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground shrink-0" />
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Propostas comerciais editadas no Canva da empresa, com o
                        PDF voltando pronto para aprovação e envio.
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            )}

            {/* WhatsApp detail */}
            {selectedIntegration === "whatsapp" && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedIntegration(null)}
                  className="gap-1.5 -ml-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Integrações
                </Button>

            {/* Caminho principal: Cadastro Incorporado da Meta (login com o Facebook). */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-green-600" />
                  Conexão do WhatsApp
                </CardTitle>
                <CardDescription>
                  Entre com o Facebook, escolha o número e pronto. Sem copiar token nem ID.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <WhatsAppConnectPanel />
              </CardContent>
            </Card>

            {/* Caminho avançado: app próprio do cliente na Meta, credenciais coladas à mão. */}
            <Collapsible open={modoAvancadoWhatsApp} onOpenChange={setModoAvancadoWhatsApp}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground">
                  <ChevronRight className={`h-4 w-4 transition-transform ${modoAvancadoWhatsApp ? "rotate-90" : ""}`} />
                  Modo avançado: usar credenciais do meu próprio app na Meta
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-4 pt-2">
            <Card className="border-amber-200 bg-amber-50/50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Lock className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-900">Só para quem tem app próprio na Meta</p>
                    <p className="text-xs text-amber-700 mt-1">
                      Estes campos substituem a conexão pelo Facebook. Preencher aqui exige um app
                      no developers.facebook.com com o produto WhatsApp e o webhook apontado para esta plataforma.
                      Se você conectou pelo botão acima, não mexa aqui.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Key className="h-4 w-4 text-primary" />
                  Credenciais da API Meta
                </CardTitle>
                <CardDescription>
                  Obtenha estas informações no painel de desenvolvedor da Meta:
                  developers.facebook.com
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <TenantWhatsAppField
                  label="Token de Acesso Permanente"
                  field="whatsappAccessToken"
                  description="Token de acesso permanente gerado no Meta Business Manager"
                  placeholder="EAAxxxxxxxxxxxxxxxx..."
                  type="password"
                  currentValue={tenantData?.hasWhatsappToken ? "••••••••(salvo)" : ""}
                  onSave={(value) => updateTenantMut.mutateAsync({ whatsappAccessToken: value })}
                  isPending={updateTenantMut.isPending}
                />
                <TenantWhatsAppField
                  label="Phone Number ID"
                  field="whatsappPhoneNumberId"
                  description="ID do número de telefone WhatsApp Business"
                  placeholder="123456789012345"
                  currentValue={tenantData?.whatsappPhoneNumberId || ""}
                  onSave={(value) => updateTenantMut.mutateAsync({ whatsappPhoneNumberId: value })}
                  isPending={updateTenantMut.isPending}
                />
                <TenantWhatsAppField
                  label="WhatsApp Business Account ID (WABA ID)"
                  field="whatsappBusinessAccountId"
                  description="ID da conta WhatsApp Business"
                  placeholder="123456789012345"
                  currentValue={tenantData?.whatsappBusinessAccountId || ""}
                  onSave={(value) => updateTenantMut.mutateAsync({ whatsappBusinessAccountId: value })}
                  isPending={updateTenantMut.isPending}
                />
                <TenantWhatsAppField
                  label="Meta App ID"
                  field="whatsappAppId"
                  description="ID do App Meta (developers.facebook.com → seu app → Configurações → Informações básicas). Necessário para enviar templates com mídia (imagem/vídeo/PDF) para aprovação."
                  placeholder="1234567890123456"
                  currentValue={tenantData?.whatsappAppId || ""}
                  onSave={(value) => updateTenantMut.mutateAsync({ whatsappAppId: value })}
                  isPending={updateTenantMut.isPending}
                />
                {/* Webhook Verify Token - gerado automaticamente, somente leitura */}
                <div className="space-y-1.5">
                  <Label className="font-medium">Webhook Verify Token</Label>
                  <p className="text-xs text-muted-foreground">
                    Gerado automaticamente pela plataforma. Cole este valor no campo <strong>"Verificar token"</strong> do painel da Meta.
                  </p>
                  <div className="flex gap-2">
                    <Input
                      readOnly
                      value={webhookTokenData?.token ?? "Carregando..."}
                      className="font-mono text-sm bg-muted/50"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="shrink-0"
                      onClick={() => navigator.clipboard.writeText(webhookTokenData?.token ?? "")}
                      disabled={!webhookTokenData?.token}
                    >
                      Copiar
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="shrink-0 text-muted-foreground hover:text-foreground"
                      onClick={() => generateTokenMut.mutate()}
                      disabled={generateTokenMut.isPending}
                      title="Gerar novo token"
                    >
                      {generateTokenMut.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    ⚠️ Se gerar um novo token, atualize também no painel da Meta para manter o webhook funcionando.
                  </p>
                </div>
                <SettingField
                  label="Versão da API"
                  keyName="meta_api_version"
                  description="Versão da Graph API a ser utilizada"
                  placeholder="v21.0"
                />

                {/* Botão de Testar Conexão */}
                <div className="pt-2 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={testConnection}
                    disabled={connectionStatus === "testing" || !phoneNumberIdSetting || !accessTokenSetting}
                  >
                    {connectionStatus === "testing" ? (
                      <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-1.5" />
                    )}
                    Testar Conexão com a Meta
                  </Button>

                  {connectionStatus !== "idle" && (
                    <div className={`flex items-start gap-2 p-3 rounded-lg text-sm mt-3 ${
                      connectionStatus === "ok" ? "bg-green-50 text-green-800 dark:bg-green-950/20 dark:text-green-300" :
                      connectionStatus === "error" ? "bg-red-50 text-red-800 dark:bg-red-950/20 dark:text-red-300" :
                      "bg-muted text-muted-foreground"
                    }`}>
                      {connectionStatus === "testing" && <Loader2 className="h-4 w-4 animate-spin shrink-0 mt-0.5" />}
                      {connectionStatus === "ok" && <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />}
                      {connectionStatus === "error" && <XCircle className="h-4 w-4 shrink-0 mt-0.5" />}
                      <div>
                        {connectionStatus === "testing" && "Testando conexão com a API Meta..."}
                        {connectionStatus === "ok" && (
                          <div>
                            <p className="font-medium">Conexão bem-sucedida!</p>
                            {connectionInfo?.displayName && <p className="text-xs">Nome: <strong>{connectionInfo.displayName}</strong></p>}
                            {connectionInfo?.phoneNumber && <p className="text-xs">Número: <strong>{connectionInfo.phoneNumber}</strong></p>}
                          </div>
                        )}
                        {connectionStatus === "error" && (
                          <div>
                            <p className="font-medium">Falha na conexão</p>
                            <p className="text-xs mt-0.5">{connectionInfo?.error}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border-blue-200 bg-blue-50/40 dark:bg-blue-950/10">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Info className="h-4 w-4 text-blue-600" />
                  URL do Webhook (Callback)
                </CardTitle>
                <CardDescription>
                  Configure esta URL no painel da Meta para receber mensagens e notificações
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="font-medium text-sm">URL de Callback do Webhook</Label>
                  <p className="text-xs text-muted-foreground">Cole esta URL no campo "Callback URL" do seu App Meta</p>
                  <div className="flex gap-2">
                    <Input
                      readOnly
                      value={`${window.location.origin}/api/webhooks/whatsapp`}
                      className="font-mono text-sm bg-muted/50 select-all"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="shrink-0"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/api/webhooks/whatsapp`);
                      }}
                    >
                      Copiar
                    </Button>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground bg-muted/50 rounded-md p-3 space-y-1">
                  <p className="font-medium text-foreground">Como configurar no painel Meta:</p>
                  <p>1. No App Meta, vá em <strong>WhatsApp &gt; Configuration &gt; Webhook</strong></p>
                  <p>2. Cole a URL acima no campo <strong>"Callback URL"</strong></p>
                  <p>3. No campo <strong>"Verificar token"</strong>, cole o <strong>Webhook Verify Token</strong> exibido acima</p>
                  <p>4. Clique em <strong>"Verify and Save"</strong> e assine os eventos: <code>messages</code></p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Info className="h-4 w-4 text-primary" />
                  Como obter as credenciais
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="text-sm space-y-2 text-muted-foreground list-decimal list-inside">
                  <li>Acesse <strong>developers.facebook.com</strong> e crie um App do tipo "Business"</li>
                  <li>Adicione o produto <strong>WhatsApp</strong> ao seu App</li>
                  <li>Em <strong>WhatsApp &gt; API Setup</strong>, copie o <strong>Phone Number ID</strong> e o <strong>WABA ID</strong></li>
                  <li>Gere um <strong>Token de Acesso Permanente</strong> no Meta Business Manager</li>
                  <li>Configure o <strong>Webhook</strong> apontando para a URL deste sistema</li>
                  <li>Cole as informações nos campos acima e salve</li>
                </ol>
              </CardContent>
            </Card>
              </CollapsibleContent>
            </Collapsible>
              </>
            )}

            {/* Email detail */}
            {selectedIntegration === "email" && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedIntegration(null)}
                  className="gap-1.5 -ml-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Integrações
                </Button>

            {/* Domínios de E-mail */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Mail className="h-4 w-4 text-primary" />
                  Domínios de E-mail
                </CardTitle>
                <CardDescription>
                  Autentique seu domínio para enviar e-mails com o endereço da sua empresa.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Adicionar domínio — limitado a 1 por tenant (regra de negócio) */}
                {emailDomains.length === 0 ? (
                  <div className="flex gap-2">
                    <Input
                      placeholder="seudominio.com"
                      value={newDomain}
                      onChange={(e) => setNewDomain(e.target.value)}
                      className="text-sm"
                      onKeyDown={(e) => e.key === "Enter" && handleAddDomain()}
                    />
                    <Button
                      size="sm"
                      className="shrink-0 gap-1"
                      onClick={handleAddDomain}
                      disabled={addingDomain || !newDomain.trim()}
                    >
                      {addingDomain ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                      Adicionar
                    </Button>
                  </div>
                ) : (
                  <div className="rounded-lg border bg-muted/40 px-3 py-2.5 text-xs text-muted-foreground">
                    Cada conta pode ter apenas <strong>1 domínio</strong> cadastrado.
                    Para trocar, remova o domínio abaixo antes de adicionar outro.
                  </div>
                )}

                {/* Lista de domínios */}
                {emailDomains.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Nenhum domínio cadastrado. Adicione um domínio acima para começar.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {emailDomains.map((d: any) => (
                      <div key={d.id} className="border rounded-lg">
                        <div className="flex items-center justify-between p-3">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-medium">{d.domain}</span>
                            <Badge variant={d.status === "verified" ? "default" : d.status === "failed" ? "destructive" : "secondary"}>
                              {d.status === "verified" ? "Verificado" : d.status === "failed" ? "Falhou" : "Pendente"}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setExpandedDomainId(expandedDomainId === d.id ? null : d.id)}
                            >
                              {expandedDomainId === d.id ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </Button>
                            {d.status !== "verified" && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-1"
                                onClick={() => handleValidateDomain(d.id)}
                                disabled={validateDomainMut.isPending}
                              >
                                {validateDomainMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                                Verificar
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-destructive"
                              onClick={() => handleDeleteDomain(d.id)}
                              disabled={deleteDomainMut.isPending}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        {/* DNS Records expandido */}
                        {expandedDomainId === d.id && (
                          <div className="border-t p-3 bg-muted/30 space-y-3">
                            <p className="text-xs text-muted-foreground font-medium">
                              Configure estes registros DNS no seu provedor (GoDaddy, Cloudflare, Registro.br, etc.):
                            </p>
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-left text-muted-foreground">
                                    <th className="pb-2 pr-3">Tipo</th>
                                    <th className="pb-2 pr-3">Host</th>
                                    <th className="pb-2 pr-3">Valor</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {buildAllDnsRecords(d.domain, d.dnsRecords as Record<string, { host: string; data: string }> | null | undefined).map((rec, i) => (
                                    <tr key={`${rec.type}-${rec.host}-${i}`} className="border-t align-top">
                                      <td className="py-2 pr-3 font-mono">{rec.type}</td>
                                      <td className="py-2 pr-3">
                                        <div className="flex items-start gap-1">
                                          <span className="font-mono break-all">{rec.host}</span>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-6 w-6 p-0 shrink-0"
                                            onClick={() => {
                                              navigator.clipboard.writeText(rec.host);
                                              toast.success("Host copiado!");
                                            }}
                                          >
                                            <Copy className="h-3 w-3" />
                                          </Button>
                                        </div>
                                      </td>
                                      <td className="py-2 pr-3">
                                        <div className="flex items-start gap-1">
                                          <span className="font-mono break-all">{rec.value}</span>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-6 w-6 p-0 shrink-0"
                                            onClick={() => {
                                              navigator.clipboard.writeText(rec.value);
                                              toast.success("Valor copiado!");
                                            }}
                                          >
                                            <Copy className="h-3 w-3" />
                                          </Button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            <p className="text-[11px] text-muted-foreground">
                              Se este domínio já envia e-mail por outro provedor (Google Workspace, outro relay),
                              adicione <code className="font-mono bg-muted px-1 rounded">include:sendgrid.net</code>{" "}
                              ao TXT de SPF existente em vez de criar um novo — múltiplos TXT de SPF invalidam o registro.
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Após configurar os registros DNS, aguarde a propagação (pode levar até 48h) e clique em "Verificar".
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Webhook SendGrid — só para super admin (config global da plataforma, não do tenant) */}
                {isSuperAdmin && (
                  <div className="pt-3 border-t space-y-3">
                    <Label className="font-medium flex items-center gap-2">
                      <Zap className="h-3.5 w-3.5" />
                      Webhook SendGrid
                      <Badge variant="outline" className="text-[10px]">Super admin</Badge>
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Configure esta URL no painel do SendGrid (Settings &gt; Mail Settings &gt; Event Webhook).
                      URL única para toda a plataforma — não é por tenant.
                    </p>
                    <div className="flex gap-2">
                      <Input
                        readOnly
                        value={sgWebhookUrl}
                        className="font-mono text-sm bg-muted/50 select-all"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        className="shrink-0"
                        onClick={() => {
                          navigator.clipboard.writeText(sgWebhookUrl);
                          toast.success("URL copiada!");
                        }}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Remetente Padrão */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Send className="h-4 w-4 text-primary" />
                  Remetente padrão
                </CardTitle>
                <CardDescription>
                  E-mail e nome que aparecerão como remetente nas campanhas. Só é possível usar domínios verificados.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {verifiedDomains.length === 0 ? (
                  <p className="text-sm text-amber-600 bg-amber-50 dark:bg-amber-950/20 rounded-lg p-3">
                    ⚠️ Você precisa ter ao menos um domínio verificado acima para configurar o remetente.
                  </p>
                ) : (
                  <>
                    <div className="space-y-1.5">
                      <Label className="font-medium">E-mail do remetente</Label>
                      <div className="flex gap-2 items-center">
                        <Input
                          placeholder="contato"
                          value={senderEmailUser}
                          onChange={(e) => setSenderEmailUser(e.target.value.trim())}
                          className="text-sm flex-1"
                        />
                        <span className="text-sm text-muted-foreground">@</span>
                        <Select value={senderEmailDomain} onValueChange={setSenderEmailDomain}>
                          <SelectTrigger className="flex-1 text-sm">
                            <SelectValue placeholder="Selecione o domínio" />
                          </SelectTrigger>
                          <SelectContent>
                            {verifiedDomains.map((d: any) => (
                              <SelectItem key={d.id} value={d.domain}>{d.domain}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {senderEmailUser && senderEmailDomain && (
                        <p className="text-xs text-muted-foreground">
                          Remetente: <strong>{senderEmailUser}@{senderEmailDomain}</strong>
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <Label className="font-medium">Nome do remetente</Label>
                      <Input
                        placeholder="Ex: Equipe da Empresa"
                        value={senderNameLocal}
                        onChange={(e) => setSenderNameLocal(e.target.value)}
                        className="text-sm"
                      />
                    </div>

                    <Button
                      onClick={handleSaveSender}
                      disabled={savingSender || !senderEmailUser || !senderEmailDomain}
                      className="w-full gap-2"
                    >
                      {savingSender ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      {savingSender ? "Salvando..." : "Salvar Remetente"}
                    </Button>

                    {senderConfig?.senderEmail && (
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-950/20 text-green-800 dark:text-green-300 text-sm">
                        <CheckCircle2 className="h-4 w-4 shrink-0" />
                        <div>
                          <p className="font-medium">Remetente ativo: {senderConfig.senderName || senderConfig.senderEmail}</p>
                          <p className="text-xs opacity-80">{senderConfig.senderEmail}</p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
              </>
            )}

            {/* Sub-página: Webhooks Externos */}
            {selectedIntegration === "webhooks" && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedIntegration(null)}
                  className="gap-1.5 -ml-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Integrações
                </Button>
                <div className="flex items-center gap-2 mb-2">
                  <Globe className="h-5 w-5 text-purple-600" />
                  <h2 className="text-lg font-semibold">Webhooks Externos</h2>
                </div>
                <WebhooksManagerPanel />
              </>
            )}

            {/* Sub-página: Chaves de API (BYOK) */}
            {selectedIntegration === "llm" && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedIntegration(null)}
                  className="gap-1.5 -ml-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Integrações
                </Button>
                <div className="flex items-center gap-2 mb-2">
                  <Key className="h-5 w-5 text-amber-600" />
                  <h2 className="text-lg font-semibold">Chaves de API (BYOK)</h2>
                </div>
                <LLMCredentialsPanel />
              </>
            )}

            {/* Sub-página: Meta Ads */}
            {selectedIntegration === "meta_ads" && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedIntegration(null)}
                  className="gap-1.5 -ml-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Integrações
                </Button>
                <MetaAdsPanel />
              </>
            )}

            {/* Sub-página: Instagram (canal) */}
            {selectedIntegration === "instagram" && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedIntegration(null)}
                  className="gap-1.5 -ml-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Integrações
                </Button>
                <InstagramPanel />
              </>
            )}

            {/* Sub-página: Canva */}
            {selectedIntegration === "canva" && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedIntegration(null)}
                  className="gap-1.5 -ml-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Integrações
                </Button>
                <CanvaPanel />
              </>
            )}
          </TabsContent>

          {/* General Tab */}
          <TabsContent value="general" className="mt-4 space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <SettingsIcon className="h-4 w-4 text-primary" />
                  Configurações Gerais
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <SettingField
                  label="Nome da Empresa"
                  keyName="company_name"
                  description="Nome exibido nas comunicações"
                  placeholder="Minha Empresa"
                />
                <SettingField
                  label="E-mail de Contato"
                  keyName="contact_email"
                  description="E-mail para notificações do sistema"
                  placeholder="admin@empresa.com"
                />
                <SettingField
                  label="Limite de Envios por Hora"
                  keyName="send_rate_limit"
                  description="Máximo de mensagens enviadas por hora (recomendado: 1000)"
                  placeholder="1000"
                />
                <SettingField
                  label="Intervalo entre Mensagens (ms)"
                  keyName="send_interval_ms"
                  description="Tempo de espera entre cada envio em milissegundos (recomendado: 100)"
                  placeholder="100"
                />
              </CardContent>
            </Card>

            <BotBehaviorCard />
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="mt-4">
            <UserManagementContent showHeader={false} />
          </TabsContent>

          {/* CRM Tab — funis, estágios, motivos de perda */}
          <TabsContent value="crm" className="mt-4 space-y-4">
            <CrmSettingsPanel />
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="mt-4 space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Bell className="h-4 w-4 text-primary" />
                  Notificações
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <SettingField
                  label="Webhook de Notificação (Slack/Discord)"
                  keyName="notification_webhook"
                  description="URL do webhook para receber alertas de campanhas e erros"
                  placeholder="https://hooks.slack.com/..."
                />
                <SettingField
                  label="E-mail para Alertas de Erro"
                  keyName="error_alert_email"
                  description="Receba e-mails quando ocorrerem falhas no envio"
                  placeholder="alertas@empresa.com"
                />
              </CardContent>
            </Card>

            <DesktopNotificationsCard />
            <AdminNotificationCategoriesCard />
          </TabsContent>

          {/* Custom Fields Tab */}
          <TabsContent value="fields" className="mt-4 space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <SettingsIcon className="h-4 w-4 text-primary" />
                      Campos Personalizados
                    </CardTitle>
                    <CardDescription className="mt-1">
                      Crie campos extras para contatos ou variáveis globais do sistema
                    </CardDescription>
                  </div>
                  <Button size="sm" className="gap-2" onClick={() => { resetFieldForm(); setFieldForm(f => ({ ...f, scope: fieldScope })); setShowFieldForm(true); }}>
                    <Plus className="h-3.5 w-3.5" />
                    Novo Campo
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Sub-tabs: Campos do Contato / Campos do Sistema */}
                <div className="flex gap-1 border-b">
                  <button
                    className={`px-3 py-1.5 text-sm font-medium border-b-2 transition-colors ${fieldScope === "contact" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                    onClick={() => { setFieldScope("contact"); resetFieldForm(); }}
                  >
                    Campos do Contato
                  </button>
                  <button
                    className={`px-3 py-1.5 text-sm font-medium border-b-2 transition-colors ${fieldScope === "system" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                    onClick={() => { setFieldScope("system"); resetFieldForm(); }}
                  >
                    Campos do Sistema
                  </button>
                </div>

                {showFieldForm && (
                  <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label>Nome do Campo *</Label>
                        <Input
                          placeholder="Ex: CPF, Cargo, Número do Contrato"
                          value={fieldForm.name}
                          onChange={e => {
                            const name = e.target.value;
                            const key = editingFieldId ? fieldForm.key : name.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
                            setFieldForm(f => ({ ...f, name, key }));
                          }}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Chave (slug) *</Label>
                        <Input
                          placeholder="cpf, cargo, numero_contrato"
                          value={fieldForm.key}
                          onChange={e => setFieldForm(f => ({ ...f, key: e.target.value }))}
                          className="font-mono text-sm"
                          disabled={!!editingFieldId}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1.5">
                        <Label>Tipo</Label>
                        <Select value={fieldForm.fieldType} onValueChange={v => setFieldForm(f => ({ ...f, fieldType: v as any }))}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="text">Texto</SelectItem>
                            <SelectItem value="number">Número</SelectItem>
                            <SelectItem value="date">Data & Hora</SelectItem>
                            <SelectItem value="boolean">Sim/Não</SelectItem>
                            <SelectItem value="select">Lista (dropdown)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label>Valor Padrão (opcional)</Label>
                        <Input
                          placeholder={fieldForm.fieldType === "boolean" ? "true ou false" : "Valor inicial..."}
                          value={fieldForm.defaultValue}
                          onChange={e => setFieldForm(f => ({ ...f, defaultValue: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Descrição (opcional)</Label>
                        <Input
                          placeholder="Descreva o campo..."
                          value={fieldForm.description}
                          onChange={e => setFieldForm(f => ({ ...f, description: e.target.value }))}
                        />
                      </div>
                    </div>
                    {fieldForm.fieldType === "select" && (
                      <div className="space-y-1.5">
                        <Label>Opções da Lista (separadas por vírgula)</Label>
                        <Input
                          placeholder="Ex: Ativo, Inativo, Pendente"
                          value={fieldForm.options}
                          onChange={e => setFieldForm(f => ({ ...f, options: e.target.value }))}
                        />
                        <p className="text-xs text-muted-foreground">Cada opção separada por vírgula aparecerá como item no dropdown</p>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        disabled={!fieldForm.name.trim() || !fieldForm.key.trim() || createFieldMut.isPending || updateFieldMut.isPending || (fieldForm.fieldType === "select" && !fieldForm.options.trim())}
                        onClick={() => {
                          const payload = {
                            name: fieldForm.name,
                            key: fieldForm.key,
                            fieldType: fieldForm.fieldType,
                            description: fieldForm.description || null,
                            defaultValue: fieldForm.defaultValue || null,
                            scope: fieldForm.scope,
                            options: fieldForm.fieldType === "select" ? fieldForm.options.split(",").map(o => o.trim()).filter(Boolean) : null,
                          };
                          if (editingFieldId) {
                            updateFieldMut.mutate({ id: editingFieldId, ...payload });
                          } else {
                            createFieldMut.mutate(payload);
                          }
                        }}
                      >
                        {(createFieldMut.isPending || updateFieldMut.isPending) ? "Salvando..." : editingFieldId ? "Atualizar Campo" : "Salvar Campo"}
                      </Button>
                      <Button size="sm" variant="outline" onClick={resetFieldForm}>Cancelar</Button>
                    </div>
                  </div>
                )}

                {(() => {
                  const scopedFields = (fieldDefs as any[]).filter((f: any) => (f.scope ?? "contact") === fieldScope);
                  return scopedFields.length === 0 && !showFieldForm ? (
                    <p className="text-sm text-muted-foreground text-center py-6">
                      {fieldScope === "contact" ? "Nenhum campo de contato criado ainda." : "Nenhum campo do sistema criado ainda."} Clique em "Novo Campo" para começar.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {scopedFields.map((f: any) => (
                        <div key={f.id} className="flex items-center justify-between border rounded-lg px-4 py-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-sm">{f.name}</span>
                              <Badge variant="outline" className="text-xs font-mono">{f.key}</Badge>
                              <Badge variant="secondary" className="text-xs">
                                {{ text: "Texto", number: "Número", date: "Data", boolean: "Sim/Não", select: "Lista" }[f.fieldType as string] ?? f.fieldType}
                              </Badge>
                              {f.defaultValue && <Badge variant="outline" className="text-xs">Padrão: {f.defaultValue}</Badge>}
                            </div>
                            {f.description && <p className="text-xs text-muted-foreground mt-0.5">{f.description}</p>}
                            {f.fieldType === "select" && f.options && (
                              <p className="text-xs text-muted-foreground mt-0.5">Opções: {(f.options as string[]).join(", ")}</p>
                            )}
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingFieldId(f.id);
                                setFieldForm({
                                  name: f.name,
                                  key: f.key,
                                  fieldType: f.fieldType,
                                  description: f.description ?? "",
                                  defaultValue: f.defaultValue ?? "",
                                  scope: f.scope ?? "contact",
                                  options: f.fieldType === "select" && f.options ? (f.options as string[]).join(", ") : "",
                                });
                                setShowFieldForm(true);
                              }}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-destructive hover:text-destructive"
                              onClick={() => deleteFieldMut.mutate({ id: f.id })}
                              disabled={deleteFieldMut.isPending}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Quick Replies Tab */}
          <TabsContent value="quick-replies" className="mt-4 space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Zap className="h-4 w-4 text-primary" />
                      Respostas Rápidas
                    </CardTitle>
                    <CardDescription className="mt-1">
                      Crie atalhos de texto para usar no Inbox. Digite "/" seguido do atalho para inserir rapidamente.
                    </CardDescription>
                  </div>
                  <Button size="sm" className="gap-2" onClick={() => { setShowQrForm(v => !v); setEditingQrId(null); setQrForm({ shortcut: "", content: "" }); }}>
                    <Plus className="h-3.5 w-3.5" />
                    Nova Resposta
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {showQrForm && (
                  <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
                    <div className="space-y-1.5">
                      <Label>Atalho *</Label>
                      <Input
                        placeholder="Ex: preco, horario, endereco"
                        value={qrForm.shortcut}
                        onChange={e => setQrForm(f => ({ ...f, shortcut: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") }))}
                        className="font-mono text-sm"
                      />
                      <p className="text-xs text-muted-foreground">Use no Inbox digitando /{qrForm.shortcut || "atalho"}</p>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Conteúdo da mensagem *</Label>
                      <Textarea
                        placeholder="Digite o texto da resposta..."
                        value={qrForm.content}
                        onChange={e => setQrForm(f => ({ ...f, content: e.target.value }))}
                        rows={3}
                        className="text-sm"
                      />
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs text-muted-foreground">Variáveis:</span>
                        {["{{nome}}", "{{telefone}}", "{{email}}"].map(v => (
                          <button
                            key={v}
                            type="button"
                            className="text-xs px-2 py-0.5 rounded-md bg-primary/10 text-primary hover:bg-primary/20 font-mono transition-colors"
                            onClick={() => setQrForm(f => ({ ...f, content: f.content + v }))}
                          >
                            {v}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        disabled={!qrForm.shortcut.trim() || !qrForm.content.trim() || createQrMut.isPending || updateQrMut.isPending}
                        onClick={() => {
                          if (editingQrId) {
                            updateQrMut.mutate({ id: editingQrId, shortcut: qrForm.shortcut, content: qrForm.content });
                          } else {
                            createQrMut.mutate({ shortcut: qrForm.shortcut, content: qrForm.content });
                          }
                        }}
                      >
                        {(createQrMut.isPending || updateQrMut.isPending) ? "Salvando..." : editingQrId ? "Atualizar" : "Salvar"}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setShowQrForm(false); setEditingQrId(null); setQrForm({ shortcut: "", content: "" }); }}>Cancelar</Button>
                    </div>
                  </div>
                )}
                {(quickReplies as any[]).length === 0 && !showQrForm ? (
                  <p className="text-sm text-muted-foreground text-center py-6">
                    Nenhuma resposta rápida criada ainda. Clique em "Nova Resposta" para começar.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {(quickReplies as any[]).map((qr: any) => (
                      <div key={qr.id} className="flex items-center justify-between border rounded-lg px-4 py-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs font-mono shrink-0">/{qr.shortcut}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{qr.content}</p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0 ml-3">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => { setEditingQrId(qr.id); setQrForm({ shortcut: qr.shortcut, content: qr.content }); setShowQrForm(true); }}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            onClick={() => deleteQrMut.mutate({ id: qr.id })}
                            disabled={deleteQrMut.isPending}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Office Hours Tab */}
          <TabsContent value="office-hours" className="mt-4 space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary" />
                  Horários de Funcionamento
                </CardTitle>
                <CardDescription className="mt-1">
                  Defina os horários de atendimento. Fora do horário, uma mensagem automática será enviada.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start justify-between gap-3 border rounded-lg px-4 py-3 bg-muted/30">
                  <div className="space-y-0.5">
                    <Label className="font-medium">Enviar mensagem automática fora do horário</Label>
                    <p className="text-xs text-muted-foreground">
                      Quando desligado, nada é enviado fora do horário e o fluxo normal segue respondendo.
                    </p>
                  </div>
                  <Switch checked={ohEnabled} onCheckedChange={setOhEnabled} />
                </div>

                <div className="space-y-2">
                  {ohDays.map((day, idx) => (
                    <div key={day.dayOfWeek} className="flex items-center gap-3 border rounded-lg px-4 py-3">
                      <Switch
                        checked={day.isOpen}
                        onCheckedChange={(checked) => setOhDays(prev => prev.map((d, i) => i === idx ? { ...d, isOpen: checked } : d))}
                      />
                      <span className="w-24 text-sm font-medium">{DAY_NAMES[day.dayOfWeek]}</span>
                      {day.isOpen ? (
                        <div className="flex items-center gap-2">
                          <Input
                            type="time"
                            value={day.startTime}
                            onChange={e => setOhDays(prev => prev.map((d, i) => i === idx ? { ...d, startTime: e.target.value } : d))}
                            className="w-28 text-sm"
                          />
                          <span className="text-muted-foreground text-sm">até</span>
                          <Input
                            type="time"
                            value={day.endTime}
                            onChange={e => setOhDays(prev => prev.map((d, i) => i === idx ? { ...d, endTime: e.target.value } : d))}
                            className="w-28 text-sm"
                          />
                        </div>
                      ) : (
                        <Badge variant="secondary" className="text-xs">Fechado</Badge>
                      )}
                    </div>
                  ))}
                </div>

                <div className="border-t pt-4 space-y-3">
                  <div className="space-y-1.5">
                    <Label className="font-medium">Mensagem fora do horário</Label>
                    <Textarea
                      placeholder="Ex: Estamos fora do horário de atendimento. Retornaremos em breve!"
                      value={ohMessage}
                      onChange={e => setOhMessage(e.target.value)}
                      rows={2}
                      className="text-sm"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="font-medium">Fluxo para fora do horário (opcional)</Label>
                    <Select value={ohFlowId} onValueChange={setOhFlowId}>
                      <SelectTrigger className="text-sm">
                        <SelectValue placeholder="Nenhum fluxo selecionado" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nenhum</SelectItem>
                        {(flowsList as any[] ?? []).map((f: any) => (
                          <SelectItem key={f.id} value={String(f.id)}>{f.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">Se definido, esse fluxo será acionado ao receber mensagem fora do horário.</p>
                  </div>
                </div>

                <Button onClick={saveOfficeHours} disabled={updateOhMut.isPending} className="gap-2">
                  {updateOhMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {updateOhMut.isPending ? "Salvando..." : "Salvar Horários"}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Default Flows Tab */}
          <TabsContent value="default-flows" className="mt-4 space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <GitBranch className="h-4 w-4 text-primary" />
                  Fluxos Padrões
                </CardTitle>
                <CardDescription className="mt-1">
                  Configure fluxos automáticos para situações específicas. Cada tipo aceita apenas um fluxo vinculado.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {([
                  { type: "welcome" as FlowTypeKey, label: "Fluxo de Boas-Vindas", desc: "Enviado apenas para novos contatos (quem nunca enviou mensagem) e somente 1x" },
                  { type: "default_reply" as FlowTypeKey, label: "Fluxo de Resposta Padrão", desc: "Enviado quando nenhuma palavra-chave é identificada" },
                  { type: "media" as FlowTypeKey, label: "Fluxo Padrão para Mídia", desc: "Enviado quando recebe imagem, vídeo, áudio ou arquivo" },
                  { type: "post_service" as FlowTypeKey, label: "Fluxo Pós-Atendimento", desc: "Acionado quando conversa é marcada como concluída no Inbox (ex: pesquisa NPS)" },
                ]).map(({ type, label, desc }) => {
                  const saved = dfSavedTypes[type];
                  const hasFlow = dfConfig[type].flowId && dfConfig[type].flowId !== "none" && dfConfig[type].flowId !== "";
                  const flowName = hasFlow ? (flowsList as any[] ?? []).find((f: any) => String(f.id) === dfConfig[type].flowId)?.name : null;
                  return (
                    <div key={type} className={`border rounded-lg px-4 py-3 space-y-2 transition-colors ${hasFlow ? "border-green-300 bg-green-50/40" : ""} ${saved ? "border-green-400 bg-green-50/70" : ""}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium">{label}</p>
                              {hasFlow ? (
                                <Badge variant="default" className="text-[10px] px-1.5 py-0 bg-green-600">{dfConfig[type].isActive ? "Ativo" : "Inativo"}</Badge>
                              ) : (
                                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Não configurado</Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">{desc}</p>
                          </div>
                        </div>
                        <Switch
                          checked={dfConfig[type].isActive}
                          onCheckedChange={(checked) => setDfConfig(prev => ({ ...prev, [type]: { ...prev[type], isActive: checked } }))}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Select
                          value={dfConfig[type].flowId}
                          onValueChange={(val) => setDfConfig(prev => ({ ...prev, [type]: { ...prev[type], flowId: val } }))}
                        >
                          <SelectTrigger className="text-sm">
                            <SelectValue placeholder="Selecionar fluxo..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Nenhum</SelectItem>
                            {(flowsList as any[] ?? []).map((f: any) => (
                              <SelectItem key={f.id} value={String(f.id)}>{f.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          size="sm"
                          disabled={updateDfMut.isPending}
                          className={`shrink-0 gap-1.5 ${saved ? "!bg-green-600 hover:!bg-green-700 !text-white" : ""}`}
                          onClick={() => {
                            const flowId = dfConfig[type].flowId && dfConfig[type].flowId !== "none" ? Number(dfConfig[type].flowId) : null;
                            updateDfMut.mutate({ flowType: type, flowId, isActive: dfConfig[type].isActive });
                          }}
                        >
                          {updateDfMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : saved ? <><CheckCircle2 className="h-3.5 w-3.5" /> Salvo!</> : "Salvar"}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Conversation Starters */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-primary" />
                      Iniciadores de Conversa
                    </CardTitle>
                    <CardDescription className="mt-1">
                      Botões de FAQ exibidos quando um novo contato abre o chat pela primeira vez (máx. 5).
                    </CardDescription>
                  </div>
                  {(csData as any[]).length < 5 && (
                    <Button size="sm" className="gap-2" onClick={() => { setShowCsForm(v => !v); setEditingCsId(null); setCsForm({ buttonText: "", flowId: "" }); }}>
                      <Plus className="h-3.5 w-3.5" />
                      Novo
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {showCsForm && (
                  <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
                    <div className="space-y-1.5">
                      <Label>Texto do botão *</Label>
                      <Input
                        placeholder="Ex: Quais são os preços?"
                        value={csForm.buttonText}
                        onChange={e => setCsForm(f => ({ ...f, buttonText: e.target.value }))}
                        maxLength={200}
                        className="text-sm"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Fluxo vinculado</Label>
                      <Select value={csForm.flowId} onValueChange={(val) => setCsForm(f => ({ ...f, flowId: val }))}>
                        <SelectTrigger className="text-sm">
                          <SelectValue placeholder="Selecionar fluxo..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Nenhum</SelectItem>
                          {(flowsList as any[] ?? []).map((f: any) => (
                            <SelectItem key={f.id} value={String(f.id)}>{f.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        disabled={!csForm.buttonText.trim() || createCsMut.isPending || updateCsMut.isPending}
                        onClick={() => {
                          const flowId = csForm.flowId && csForm.flowId !== "none" ? Number(csForm.flowId) : null;
                          if (editingCsId) {
                            updateCsMut.mutate({ id: editingCsId, buttonText: csForm.buttonText, flowId });
                          } else {
                            createCsMut.mutate({ buttonText: csForm.buttonText, flowId, sortOrder: (csData as any[]).length });
                          }
                        }}
                      >
                        {(createCsMut.isPending || updateCsMut.isPending) ? "Salvando..." : editingCsId ? "Atualizar" : "Salvar"}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setShowCsForm(false); setEditingCsId(null); setCsForm({ buttonText: "", flowId: "" }); }}>Cancelar</Button>
                    </div>
                  </div>
                )}
                {(csData as any[]).length === 0 && !showCsForm ? (
                  <p className="text-sm text-muted-foreground text-center py-6">
                    Nenhum iniciador de conversa criado. Clique em "Novo" para começar.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {(csData as any[]).map((cs: any) => (
                      <div key={cs.id} className="flex items-center justify-between border rounded-lg px-4 py-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{cs.buttonText}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {cs.flowId ? `Fluxo #${cs.flowId}` : "Sem fluxo vinculado"}
                            {!cs.isActive && " · Inativo"}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0 ml-3">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => { setEditingCsId(cs.id); setCsForm({ buttonText: cs.buttonText, flowId: cs.flowId ? String(cs.flowId) : "" }); setShowCsForm(true); }}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            onClick={() => deleteCsMut.mutate({ id: cs.id })}
                            disabled={deleteCsMut.isPending}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
    </PermissionGuard>
  );
}
